<template>
	<MenuLayout>
		<template #activator="{ click, }">
			<slot name="button" :onClick="click">
				<EllipsisButton
					:class="iconClasses"
					size="SMALL"
					type="QUATERNARY"
					@click="click"
				/>
			</slot>
		</template>
		<template #menu="{ hide, }">
			<div class="pop-menu__actions-menu">
				<ul class="pop-menu__options" ref="options">
					<li v-for="option in options"
						:key="option[ altValueField || 'value' ]"
						:class="menuItemClass( option )"
						@click="onOptionClicked( { option, hide, }, $event )"
					>
						<slot name="options" :option="option" :onOptionClicked="onOptionClicked">
							<div v-if="option.isSeparator" class="pop-menu__option--is-separator"></div>
							<div v-else-if="option.tooltip" v-tooltip="option.tooltip">{{ option.text }}</div>
							<div v-else>{{ option.text }}</div>
							<BetaBadge v-if="option.beta" />
							<FontAwesomeIcon v-if="option.icon"
								class="pop-menu-icon"
								:icon="option.icon"
							/>
						</slot>
					</li>
				</ul>
			</div>
		</template>
	</MenuLayout>
</template>

<script>
import MenuLayout from "./MenuLayout.vue";
import BetaBadge from "./BetaBadge.vue";
import EllipsisButton from "./buttons/EllipsisButton.vue";

export const makeOption = ( {
	text = "",
	value = "",
	beta = false,
	destructive = false,
	icon = null, // font awesome icon array
	isSeparator = false,
	tooltip = "",
	disabled = false,
} = {} ) => ( {
	text,
	value,
	beta,
	destructive,
	icon,
	isSeparator,
	disabled,
	tooltip
} );

export default {
	name: "PopMenu",
	emits: [ "select", ],
	components: {
		MenuLayout,
		EllipsisButton,
		BetaBadge,
	},
	props: {
		/**
		 * @see makeOption
		 */
		options: {
			type: Array,
			required: true,
		},
		altValueField: {
			type: String,
			default: "",
		},
		isHidden: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		iconClasses() {
			return {
				"pop-menu__actions-icon": true,
				"pop-menu__actions-icon--hidden": this.isHidden,
			};
		},
	},
	methods: {
		menuItemClass( option ) {
			return {
				"pop-menu__option": !option.isSeparator,
				"pop-menu__option--destructive": !!option.destructive,
				"pop-menu__option--separator": option.isSeparator,
				"pop-menu__option--disabled": option.disabled,
			};
		},
		onOptionClicked( { option, hide, }, e ) {
			e.stopPropagation();
			if (!option.isSeparator && !option.disabled) {
				hide();
				this.$emit( "select", { ...option, } );
			} else {
				e.preventDefault();
			}
		},
	},
};
</script>

<style lang="scss" scoped>
$iconColor: $color1-500;

.pop-menu__options {
	z-index: 10;
	list-style: none;
	margin: 0;
	padding: 0;
	background-color: $popmenu-background-color;
	border: 0.0625rem solid $popmenu-border-color;
	border-radius: 0.5rem;
	text-align: left;
	box-shadow: 0 0.1875rem 0.375rem 0.0625rem rgba(33, 45, 99, 0.1), 0 1.313rem 1.875rem -1.25rem rgba(33, 45, 99, 0.2);
	color: $popmenu-font-color;
	cursor: pointer;
}

.pop-menu__actions-icon--hidden {
	visibility: hidden;
}

.pop-menu__option {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	gap: 0.5rem;

	padding: 0.375rem 1rem; // 6px 16px

	&:first-of-type {
		margin-top: 0.375rem; // 6px
	}

	&:last-of-type {
		margin-bottom: 0.375rem; // 6px
	}

	&:hover {
		background-color: $popmenu-background-color--hover;
	}
}

.pop-menu__option--destructive {
	color: $destructive-font-color;
}

.pop-menu__option--disabled {
	color: $color1-400;
	cursor: not-allowed;
	&:hover {
		background-color: $popmenu-background-color;
	}
}

.pop-menu__option--separator {
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: 0.5rem;
	padding: 0.375rem;
	.pop-menu__option--is-separator {
		width: 100%;
		border-top: 0.0625rem solid $popmenu-border-color;
	}
}

.pop-menu-icon {
	color: $iconColor;
}
</style>
